import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Copy, GripVertical, Link2, Plus, Send, Trash2 } from 'lucide-react';
import { PageHeader, ErrorState, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select, Separator, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { currencyLabel } from '@/lib/currencies';
import { formatDate, formatMoney } from '@/lib/format';
import {
  useCompanyOptions,
  useContactOptions,
  useQuote,
  useReviseQuote,
  useSaveQuote,
  useSendQuote,
} from './api';
import { QUOTE_STATUS_LABELS, QUOTE_STATUS_VARIANT } from './labels';

type DraftItem = {
  section: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: string;
  discountType: 'PERCENT' | 'AMOUNT';
  discountValue: number;
  taxRatePercent: number;
  isOptional: boolean;
};

const BLANK_ITEM: DraftItem = {
  section: '',
  description: '',
  quantity: 1,
  unit: '',
  unitPrice: '',
  discountType: 'PERCENT',
  discountValue: 0,
  taxRatePercent: 5,
  isOptional: false,
};

/** Locked once the client has seen it — a change means a new version. */
const LOCKED = new Set(['SENT', 'VIEWED', 'ACCEPTED', 'REJECTED', 'EXPIRED', 'REVISED']);

export function QuoteBuilderPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const isNew = !quoteId || quoteId === 'new';
  const { data: quote, isLoading, error, refetch } = useQuote(isNew ? undefined : quoteId);
  const save = useSaveQuote();
  const revise = useReviseQuote();
  const send = useSendQuote();

  const { data: companies = [] } = useCompanyOptions();
  const [companyId, setCompanyId] = useState(params.get('companyId') ?? '');
  const { data: contacts = [] } = useContactOptions(companyId || undefined);

  const [contactId, setContactId] = useState('');
  const [dealId] = useState(params.get('dealId') ?? '');
  const [validUntil, setValidUntil] = useState('');
  const [currency, setCurrency] = useState('AED');
  const [terms, setTerms] = useState('');
  const [notes, setNotes] = useState('');
  const [assumptions, setAssumptions] = useState('');
  const [exclusions, setExclusions] = useState('');
  const [items, setItems] = useState<DraftItem[]>([{ ...BLANK_ITEM }]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Hydrate the form once the quote arrives.
  useEffect(() => {
    if (!quote) return;
    setCompanyId(quote.companyId);
    setContactId(quote.contactId ?? '');
    setValidUntil(quote.validUntil?.slice(0, 10) ?? '');
    setCurrency(quote.currency);
    setTerms(quote.terms ?? '');
    setNotes(quote.notes ?? '');
    setAssumptions(quote.assumptions ?? '');
    setExclusions(quote.exclusions ?? '');
    setItems(
      quote.items.map((item) => ({
        section: item.section ?? '',
        description: item.description,
        quantity: item.quantity,
        unit: item.unit ?? '',
        unitPrice: item.unitPrice ?? '',
        discountType: item.discountType,
        discountValue: item.discountValue,
        taxRatePercent: item.taxRatePercent,
        isOptional: item.isOptional,
      })),
    );
  }, [quote]);

  if (!isNew && isLoading) {
    return (
      <div>
        <PageHeader title="Quote" />
        <TableSkeleton />
      </div>
    );
  }
  if (!isNew && (error || !quote)) {
    return <ErrorState message="Could not load this quote." onRetry={() => void refetch()} />;
  }

  const locked = quote ? LOCKED.has(quote.status) : false;
  const billTo = companies.find((option) => option.id === companyId);
  // The client's own currency first, then anything else they are billed in.
  const billable = billTo ? [billTo.currency, ...billTo.currencies] : [];
  const totals = computeTotals(items);

  const setItem = (index: number, patch: Partial<DraftItem>) =>
    setItems((current) =>
      current.map((item, position) => (position === index ? { ...item, ...patch } : item)),
    );

  const submit = async () => {
    setSaveError(null);
    try {
      const saved = await save.mutateAsync({
        ...(quote && !locked ? { id: quote.id } : {}),
        companyId,
        contactId,
        dealId: quote?.dealId ?? dealId,
        validUntil,
        currency,
        terms,
        notes,
        assumptions,
        exclusions,
        items: items
          .filter((item) => item.description.trim().length > 0)
          .map((item) => ({
            section: item.section,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice || '0',
            discountType: item.discountType,
            discountValue: item.discountValue,
            taxRatePercent: item.taxRatePercent,
            isOptional: item.isOptional,
          })),
      });
      navigate(`/crm/quotes/${saved.id}`);
    } catch (caught) {
      setSaveError(caught instanceof ApiRequestError ? caught.message : 'Could not save the quote.');
    }
  };

  const publicLink = quote ? `${window.location.origin}/q/${quote.publicToken}` : '';

  return (
    <div>
      <PageHeader
        title={quote ? `${quote.number}${quote.version > 1 ? ` v${quote.version}` : ''}` : 'New quote'}
        breadcrumbs={[{ label: 'Quotes', to: '/crm/quotes' }, { label: quote?.number ?? 'New' }]}
        description={quote ? quote.company.name : 'Build a proposal for a client.'}
        actions={
          quote && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={QUOTE_STATUS_VARIANT[quote.status]}>
                {QUOTE_STATUS_LABELS[quote.status]}
              </Badge>
              {locked && (
                <Can permission="crm.quote.create">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={quote.status === 'ACCEPTED' || revise.isPending}
                    onClick={async () => {
                      const next = await revise.mutateAsync(quote.id);
                      navigate(`/crm/quotes/${next.id}`);
                    }}
                  >
                    <Copy className="h-4 w-4" />
                    New version
                  </Button>
                </Can>
              )}
              {!locked && (
                <Can permission="crm.quote.send">
                  <Button
                    size="sm"
                    disabled={send.isPending || !quote.contactId}
                    title={quote.contactId ? undefined : 'Add a contact with an email first'}
                    onClick={() => send.mutate(quote.id)}
                  >
                    <Send className="h-4 w-4" />
                    {send.isPending ? 'Sending…' : 'Send to client'}
                  </Button>
                </Can>
              )}
            </div>
          )
        }
      />

      {quote && quote.sentAt && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-md border bg-muted/30 px-4 py-2.5 text-sm">
          <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <code className="min-w-0 flex-1 truncate text-xs">{publicLink}</code>
          <button
            onClick={() => {
              void navigator.clipboard.writeText(publicLink);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            }}
            className="shrink-0 text-xs text-primary hover:underline"
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
          {quote.viewCount > 0 && (
            <span className="shrink-0 text-xs text-muted-foreground">
              Opened {quote.viewCount} {quote.viewCount === 1 ? 'time' : 'times'}
            </span>
          )}
        </div>
      )}

      {quote?.acceptedAt && (
        <div className="mb-5 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          Accepted by <strong>{quote.acceptedByName}</strong> on {formatDate(quote.acceptedAt)}.
        </div>
      )}
      {quote?.rejectedAt && (
        <div className="mb-5 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Rejected on {formatDate(quote.rejectedAt)}
          {quote.rejectionReason ? ` — ${quote.rejectionReason}` : ''}.
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[1fr_18rem]">
        <div className="space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Client</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Company</Label>
                <Select
                  value={companyId}
                  disabled={locked || Boolean(quote)}
                  onChange={(event) => {
                    setCompanyId(event.target.value);
                    setContactId('');
                    // A quote is priced in what this client is billed in.
                    const picked = companies.find((option) => option.id === event.target.value);
                    if (picked) setCurrency(picked.currency);
                  }}
                >
                  <option value="">Choose a company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Contact</Label>
                <Select
                  value={contactId}
                  disabled={locked || !companyId}
                  onChange={(event) => setContactId(event.target.value)}
                >
                  <option value="">Not set</option>
                  {contacts.map((contact) => (
                    <option key={contact.id} value={contact.id}>
                      {contact.name}
                      {contact.email ? ` — ${contact.email}` : ''}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">
                  The quote is emailed to this person.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Valid until</Label>
                <Input
                  type="date"
                  disabled={locked}
                  value={validUntil}
                  onChange={(event) => setValidUntil(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <Select
                  disabled={locked || billable.length < 2}
                  value={currency}
                  onChange={(event) => setCurrency(event.target.value)}
                >
                  {/* A quote already priced in a currency keeps it. */}
                  {!billable.includes(currency) && <option value={currency}>{currency}</option>}
                  {billable.map((code) => (
                    <option key={code} value={code}>
                      {currencyLabel(code)}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground">
                  {billable.length > 1
                    ? 'This client is billed in more than one currency.'
                    : 'Set on the company record.'}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">Line items</CardTitle>
              {!locked && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setItems((current) => [...current, { ...BLANK_ITEM }])}
                >
                  <Plus className="h-4 w-4" />
                  Add line
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {items.map((item, index) => (
                <div key={index} className="rounded-md border p-3">
                  <div className="flex items-start gap-2">
                    <GripVertical className="mt-2 h-4 w-4 shrink-0 text-muted-foreground/50" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="grid gap-2 sm:grid-cols-[1fr_10rem]">
                        <Input
                          placeholder="Description"
                          disabled={locked}
                          value={item.description}
                          onChange={(event) =>
                            setItem(index, { description: event.target.value })
                          }
                        />
                        <Input
                          placeholder="Section (optional)"
                          disabled={locked}
                          value={item.section}
                          onChange={(event) => setItem(index, { section: event.target.value })}
                        />
                      </div>

                      <div className="grid gap-2 sm:grid-cols-5">
                        <LabelledInput
                          label="Qty"
                          type="number"
                          min={0}
                          step="0.01"
                          disabled={locked}
                          value={item.quantity}
                          onChange={(value) => setItem(index, { quantity: Number(value) })}
                        />
                        <LabelledInput
                          label="Unit price"
                          inputMode="decimal"
                          disabled={locked}
                          value={item.unitPrice}
                          onChange={(value) => setItem(index, { unitPrice: value })}
                        />
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Discount</Label>
                          <div className="flex gap-1">
                            <Input
                              type="number"
                              min={0}
                              className="h-8"
                              disabled={locked}
                              value={item.discountValue}
                              onChange={(event) =>
                                setItem(index, { discountValue: Number(event.target.value) })
                              }
                            />
                            <Select
                              className="h-8 w-16 px-1 text-xs"
                              disabled={locked}
                              value={item.discountType}
                              onChange={(event) =>
                                setItem(index, {
                                  discountType: event.target.value as 'PERCENT' | 'AMOUNT',
                                })
                              }
                            >
                              <option value="PERCENT">%</option>
                              <option value="AMOUNT">{currency}</option>
                            </Select>
                          </div>
                        </div>
                        <LabelledInput
                          label="Tax %"
                          type="number"
                          min={0}
                          max={100}
                          disabled={locked}
                          value={item.taxRatePercent}
                          onChange={(value) => setItem(index, { taxRatePercent: Number(value) })}
                        />
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Line total</Label>
                          <div className="flex h-8 items-center justify-end tabular text-sm font-medium">
                            {formatMoney(lineTotal(item), currency)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                          <Checkbox
                            checked={item.isOptional}
                            disabled={locked}
                            onCheckedChange={(checked) =>
                              setItem(index, { isOptional: checked === true })
                            }
                          />
                          Optional — the client can choose to include this
                        </label>
                        {!locked && items.length > 1 && (
                          <button
                            onClick={() =>
                              setItems((current) => current.filter((_, i) => i !== index))
                            }
                            className="text-xs text-destructive hover:underline"
                          >
                            <Trash2 className="inline h-3 w-3" /> Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Terms and scope</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Terms and conditions</Label>
                <Textarea
                  rows={3}
                  disabled={locked}
                  value={terms}
                  onChange={(event) => setTerms(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Assumptions</Label>
                <Textarea
                  rows={3}
                  disabled={locked}
                  value={assumptions}
                  placeholder="What this estimate takes as given"
                  onChange={(event) => setAssumptions(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Exclusions</Label>
                <Textarea
                  rows={3}
                  disabled={locked}
                  value={exclusions}
                  placeholder="What is explicitly not included"
                  onChange={(event) => setExclusions(event.target.value)}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Internal notes</Label>
                <Textarea
                  rows={2}
                  disabled={locked}
                  value={notes}
                  placeholder="Never shown to the client"
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="lg:sticky lg:top-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Total</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Subtotal" value={formatMoney(totals.subtotal, currency)} />
              <Row label="Discount" value={`− ${formatMoney(totals.discount, currency)}`} />
              <Row label="Tax" value={formatMoney(totals.tax, currency)} />
              <Separator />
              <div className="flex items-center justify-between text-base font-semibold">
                <span>Grand total</span>
                <span className="tabular">{formatMoney(totals.grand, currency)}</span>
              </div>
              {items.some((item) => item.isOptional) && (
                <p className="text-xs text-muted-foreground">
                  Optional lines are included above and can be toggled by the client.
                </p>
              )}

              <FormError message={saveError} />

              {!locked && (
                <Can permission="crm.quote.create">
                  <Button
                    className="mt-2 w-full"
                    onClick={submit}
                    disabled={
                      save.isPending ||
                      !companyId ||
                      items.every((item) => item.description.trim().length === 0)
                    }
                  >
                    {save.isPending ? 'Saving…' : quote ? 'Save quote' : 'Create quote'}
                  </Button>
                </Can>
              )}
            </CardContent>
          </Card>

          {quote?.versions && quote.versions.length > 1 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Versions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {quote.versions.map((version) => (
                  <button
                    key={version.id}
                    onClick={() => navigate(`/crm/quotes/${version.id}`)}
                    className={
                      version.id === quote.id
                        ? 'flex w-full items-center justify-between rounded-md border border-primary bg-primary/5 px-3 py-2 text-sm'
                        : 'flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-accent'
                    }
                  >
                    <span>v{version.version}</span>
                    <span className="flex items-center gap-2">
                      <span className="tabular text-xs">
                        {formatMoney(version.grandTotal, quote.currency)}
                      </span>
                      <Badge variant={QUOTE_STATUS_VARIANT[version.status]} className="text-[10px]">
                        {QUOTE_STATUS_LABELS[version.status]}
                      </Badge>
                    </span>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Totals, mirroring the server so the preview matches what is saved ────────

function lineTotal(item: DraftItem): string {
  const gross = toMinor(item.unitPrice) * item.quantity;
  const discount =
    item.discountType === 'AMOUNT' ? toMinor(String(item.discountValue)) : (gross * item.discountValue) / 100;
  const net = gross - discount;
  return fromMinor(Math.round(net + (net * item.taxRatePercent) / 100));
}

function computeTotals(items: DraftItem[]) {
  let subtotal = 0;
  let discount = 0;
  let tax = 0;

  for (const item of items) {
    const gross = toMinor(item.unitPrice) * item.quantity;
    const itemDiscount =
      item.discountType === 'AMOUNT'
        ? toMinor(String(item.discountValue))
        : (gross * item.discountValue) / 100;
    const net = gross - itemDiscount;
    subtotal += gross;
    discount += itemDiscount;
    tax += (net * item.taxRatePercent) / 100;
  }

  return {
    subtotal: fromMinor(Math.round(subtotal)),
    discount: fromMinor(Math.round(discount)),
    tax: fromMinor(Math.round(tax)),
    grand: fromMinor(Math.round(subtotal - discount + tax)),
  };
}

function toMinor(value: string): number {
  const parsed = Number((value || '0').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

function fromMinor(minor: number): string {
  return (minor / 100).toFixed(2);
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular">{value}</span>
    </div>
  );
}

function LabelledInput({
  label,
  value,
  onChange,
  ...props
}: {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] text-muted-foreground">{label}</Label>
      <Input
        className="h-8"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        {...props}
      />
    </div>
  );
}
